import React, { useState } from 'react';
import styled from 'styled-components';
import { VotingRound } from '../../../domain/entities/Room';
import { TokenSet } from '../../styles/tokens';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCardLabel } from '../../i18n/cardLabels';

const Panel = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 12px;
  overflow: hidden;
`;

const PanelHeader = styled.button<{ tokens: TokenSet }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.tokens.text};
  font-size: 14px;
  font-weight: 700;
`;

const Chevron = styled.span<{ open: boolean }>`
  transform: rotate(${p => p.open ? 180 : 0}deg);
  transition: transform 0.2s;
`;

const RoundItem = styled.div<{ tokens: TokenSet }>`
  padding: 8px 14px;
  border-top: 1px solid ${p => p.tokens.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const RoundLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const RoundStory = styled.div<{ tokens: TokenSet }>`
  font-size: 13px;
  color: ${p => p.tokens.text};
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeckTag = styled.span<{ tokens: TokenSet }>`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.tokens.textMuted};
  background: ${p => p.tokens.bgElevated};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 8px;
  padding: 2px 6px;
  width: fit-content;
`;

const RoundEstimate = styled.div<{ tokens: TokenSet }>`
  font-size: 17px;
  font-weight: 800;
  color: ${p => p.tokens.primary};
`;

interface VotingHistoryProps {
  history: VotingRound[];
  tokens: TokenSet;
}

export const VotingHistory: React.FC<VotingHistoryProps> = ({ history, tokens }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  if (!history || history.length === 0) return null;

  return (
    <Panel tokens={tokens}>
      <PanelHeader tokens={tokens} onClick={() => setOpen(o => !o)}>
        <span>{t.historyTitle(history.length)}</span>
        <Chevron open={open}>▼</Chevron>
      </PanelHeader>

      {open && [...history].reverse().map((round, i) => {
        const deckType = round.deckType || 'fibonacci';
        const label = round.finalEstimate
          ? getCardLabel(round.finalEstimate, deckType, t)
          : '?';
        return (
          <RoundItem key={i} tokens={tokens}>
            <RoundLeft>
              <RoundStory tokens={tokens}>{round.story || t.noTitle}</RoundStory>
              <DeckTag tokens={tokens}>
                {deckType === 'tshirt' ? t.deck.tshirt : t.deck.fibonacci}
              </DeckTag>
            </RoundLeft>
            <RoundEstimate tokens={tokens}>{label}</RoundEstimate>
          </RoundItem>
        );
      })}
    </Panel>
  );
};
